import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getProfile } from '../lib/api';
import type { CustomTheme } from '@pcp/shared';
import { useThemeStore, type ThemePalette, type ThemeMode, type ThemeContrast } from '../lib/stores';
import { useAuthStore } from '../lib/authStore';

// Hydrates the per-user theme from the server once authenticated. localStorage
// still drives the instant pre-paint theme; this reconciles it to the user's
// server-saved value (e.g. one set on another device). Renders nothing.
export function ThemeSync() {
  const status = useAuthStore((s) => s.status);
  const { data } = useQuery({
    queryKey: ['profile'],
    queryFn: getProfile,
    enabled: status === 'authenticated',
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!data) return;
    const current = useThemeStore.getState();
    const patch: {
      palette?: ThemePalette; mode?: ThemeMode; contrast?: ThemeContrast;
      customThemes?: CustomTheme[]; activeCustomId?: string | null;
    } = {};
    if (data.theme.palette && data.theme.palette !== current.palette) patch.palette = data.theme.palette;
    if (data.theme.mode && data.theme.mode !== current.mode) patch.mode = data.theme.mode;
    if (data.theme.contrast && data.theme.contrast !== current.contrast) patch.contrast = data.theme.contrast;
    // Custom-theme library + selection sync across devices (server is source of
    // truth; saves push to the server so this resolves to a no-op after a save).
    if (Array.isArray(data.theme.customThemes)
      && JSON.stringify(data.theme.customThemes) !== JSON.stringify(current.customThemes)) {
      patch.customThemes = data.theme.customThemes;
    }
    if (data.theme.activeCustomId !== undefined && data.theme.activeCustomId !== current.activeCustomId) {
      patch.activeCustomId = data.theme.activeCustomId ?? null;
    }
    if (Object.keys(patch).length) useThemeStore.setState(patch);
  }, [data]);

  return null;
}
